import { EventType } from "@/analytics/analytics-events";
import { CopyCoordinateProperties } from "@/analytics/place-pin-events-properties";
import { useDistanceUnit } from "@/components/common/settings-context/settings-context";
import { ANNOTATION_ZINDEX_RANGE_MAP } from "@/components/ui/annotations/annotation-zindex-range-map";
import {
  EmbeddedToolbar,
  EmbeddedToolbarButton,
  EmbeddedToolbarVerticalDivider,
} from "@/components/ui/embedded-toolbar";
import {
  formatSparseCoordinateString,
  getCoordinateStringZUp,
} from "@/components/ui/scene-context-menu";
import { setPinMarker } from "@/store/pin-marker-slice";
import { useAppDispatch } from "@/store/store-hooks";
import {
  DeleteIcon,
  FaroText,
  grey,
  useToast,
} from "@faro-lotv/app-component-toolbox";
import { Analytics } from "@faro-lotv/foreign-observers";
import { Tooltip } from "@mui/material";
import { RefObject, useCallback, useMemo, useState, type JSX } from "react";
import { Vector3Tuple } from "three";
import { AppAwareHtml } from "../app-aware-html";
import { MEASURE_ANIMATION_LENGTH } from "../measurements/measure-constants";

type PinMarkerActionBarProp = {
  /** 3D position of the pin marker */
  pinPosition: Vector3Tuple;

  /** The parent that the toolbar should have in the html DOM */
  parentRef: RefObject<HTMLElement>;
};

/**
 * @returns Action toolbar for pin marker.
 */
export function PinMarkerActionBar({
  pinPosition,
  parentRef,
}: PinMarkerActionBarProp): JSX.Element | null {
  const { openToast } = useToast();

  const buttonSize = "36px";
  const actionBarPointOffsetX = "60px";
  const actionBarPointOffsetY = "40px";

  const dispatch = useAppDispatch();

  const deletePinMarker = useCallback(() => {
    Analytics.track(EventType.deletePin);
    dispatch(setPinMarker());
  }, [dispatch]);

  const [sparseCoordinateString, setSparseCoordinateString] =
    useState<string>();

  const distanceUnit = useDistanceUnit();
  const coordinateString = useMemo(() => {
    const coordStr = getCoordinateStringZUp(pinPosition, distanceUnit, false);
    setSparseCoordinateString(
      formatSparseCoordinateString(pinPosition, distanceUnit),
    );
    return coordStr;
  }, [distanceUnit, pinPosition]);

  const copyCoordinates = useCallback((): void => {
    Analytics.track<CopyCoordinateProperties>(EventType.copyCoordinate, {
      via: "pin toolbar",
    });

    navigator.clipboard.writeText(coordinateString);
    openToast({ title: "Coordinates copied to clipboard" });
  }, [coordinateString, openToast]);

  return (
    <AppAwareHtml
      portal={parentRef}
      position={pinPosition}
      zIndexRange={ANNOTATION_ZINDEX_RANGE_MAP.toolbar}
      eps={-1}
      style={{
        transform: `translate(-${actionBarPointOffsetX}, calc(-100% - ${actionBarPointOffsetY}))`,
      }}
    >
      <EmbeddedToolbar
        isActive
        sx={{
          transition: `opacity ${MEASURE_ANIMATION_LENGTH}s linear`,
          padding: "0px",
          backgroundColor: grey[1000],
          borderRadius: "6px",
        }}
      >
        <Tooltip title="Copy coordinates" placement="top">
          <EmbeddedToolbarButton
            aria-label="copy coordinates"
            fullWidth
            value={coordinateString}
            onClick={copyCoordinates}
          >
            <FaroText
              variant="bodyM"
              color={grey[0]}
              sx={{ whiteSpace: "nowrap", width: "auto", ml: 1, mr: 1 }}
            >
              {sparseCoordinateString}
            </FaroText>
          </EmbeddedToolbarButton>
        </Tooltip>
        <EmbeddedToolbarVerticalDivider />
        <Tooltip title="Delete" placement="top">
          <EmbeddedToolbarButton
            aria-label="delete pin marker"
            buttonSize={buttonSize}
            value="Delete"
            onClick={deletePinMarker}
          >
            <DeleteIcon sx={{ ml: 1, mr: 1 }} />
          </EmbeddedToolbarButton>
        </Tooltip>
      </EmbeddedToolbar>
    </AppAwareHtml>
  );
}
